import React, { Dispatch, SetStateAction, useState } from "react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import { Box, Flex, Text } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  customsTrackingUrl,
  internationalTrackingUrls,
} from "@/features/my-order/features/constants";
import { extractTransportationFromOrderHistory } from "@/features/my-order/features/order-history/utils";
import useOrderHistoryProformaInvoice from "@/features/my-order/features/order-history/hooks/useOrderHistoryProformaInvoice";
import { toCamelKeyObject } from "@/utils/case";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  variant: "DOMESTIC" | "INTERNATIONAL" | "CUSTOMS";
  transportationId?: number;
}

interface TrackingModalDetail {
  title: string;
  code?: string[];
  url?: string;
  name?: string | null;
}

function OrderHistoryTransportationTrackingModal({
  isOpen,
  onClose,
  variant,
  transportationId,
}: Props) {
  const [isCopied, setIsCopied] = useState(false);
  const { tTitle } = useI18nHelper();
  const { data } = useOrderHistoryProformaInvoice();

  const {
    mAwbNumber,
    houseBlNumber,
    domesticTrackingNumber,
    domesticCarrierName,
  } = extractTransportationFromOrderHistory(
    toCamelKeyObject(
      data?.transportations.find((item) => item.id === transportationId)
    )
  );

  const flightCode = mAwbNumber?.slice(0, 3);

  const variantMap: Record<string, TrackingModalDetail> = {
    INTERNATIONAL: {
      title: "international_tracking",
      code: [mAwbNumber!],
      url: internationalTrackingUrls[
        flightCode! as keyof typeof internationalTrackingUrls
      ],
    },
    CUSTOMS: {
      title: "customs_status",
      code: houseBlNumber,
      url: customsTrackingUrl,
    },
    DOMESTIC: {
      title: "domestic_tracking",
      code: [domesticTrackingNumber!],
      name: domesticCarrierName,
    },
  };

  const handleClose = () => {
    setIsCopied(false);
    onClose();
  };

  const handleCopy = async (
    text: string,
    setCopied: Dispatch<SetStateAction<boolean>>
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        title={tTitle(`order_history:invoice.${variantMap[variant].title}`)}
        onClose={handleClose}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"4px"} alignItems={"center"}>
          {variantMap[variant].name && (
            <Text textStyle={"body-md-semi-bold"} color={"#444444"}>
              {variantMap[variant].name}
            </Text>
          )}
          {variantMap[variant].code?.map((item, index) => (
            <Flex key={index} gap={"8px"} alignItems={"center"}>
              <Text textStyle={"body-md-medium"} color={"#444444"}>
                {item}
              </Text>
              <Flex
                gap={"2px"}
                alignItems={"center"}
                cursor={"pointer"}
                className={"group"}
                onClick={() => handleCopy(item, setIsCopied)}
              >
                <Box
                  _groupHover={{
                    color: "#1272EF",
                  }}
                  color={"#8F8F8C"}
                >
                  <CustomIcon
                    src={isCopied ? "check" : "copy"}
                    width={16}
                    height={16}
                  />
                </Box>
                <Text
                  textStyle={"body-sm-medium"}
                  color={"#444444"}
                  _groupHover={{
                    color: isCopied ? "#444444" : "#1272EF",
                  }}
                >
                  {isCopied
                    ? tTitle("order_history:invoice.copied")
                    : tTitle("order_history:invoice.copy")}
                </Text>
              </Flex>
            </Flex>
          ))}
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        confirmName={"조회"}
        cancelName={"닫기"}
        onCancel={handleClose}
        onConfirm={
          variant === "DOMESTIC"
            ? undefined
            : () => window.open(variantMap[variant].url, "_blank")
        }
      />
    </CustomApprovalModal>
  );
}

export default OrderHistoryTransportationTrackingModal;
